import { track } from '@/lib/usage-tracker';

let lastWidgetTrack = 0;
import { create } from 'zustand';
import { User, Plant, Device, DashWidget, Lang, PageId, ThemeColor } from '@/lib/types';
import { INITIAL_USERS, INITIAL_PLANTS } from '@/lib/demo-data';
import { userlevelToRole } from '@/lib/permissions';
import { DEV_TYPES } from '@/lib/device-types';
import { deleteWidgetImage } from '@/lib/widget-image';
import { loadNotifications, saveNotifications } from '@/lib/notif-storage';

/** สำเร็จ · ล้มเหลว · แจ้งให้ทราบ — คุมสีและไอคอนของ popup */
export type ToastKind = 'success' | 'error' | 'info';

export interface DbUser {
  id: number;
  name: string;
  email: string;
  userlevel: string | null;
  /** server ตัดสินให้แล้ว — หน้าเว็บอย่าคิดเองซ้ำ (lib/session-flags.ts) */
  canManageUsers?: boolean;
  /** ฟีเจอร์ที่แพ็กเกจของบัญชีนี้เปิดให้ */
  features?: { pm: boolean; ai: boolean; autoReport: boolean; calibration: boolean };
}

// ── Solution → dashboard preset (pillar ③ · slice 2) ─────────────────────────
// Given a plant's chosen solution + its linked devices, return a starter widget
// layout shaped by that solution. Returns null to fall through to the legacy
// device-heuristic provisioning (so nothing breaks when solution is unset or no
// representative device is linked yet).
function solutionPreset(solution: string, devs: Device[], th: boolean): DashWidget[] | null {
  const pick = (types: string[]) => devs.find((d) => types.includes(d.type));
  // Cards built from a device's actual data keys — robust to unknown field sets.
  const cardsFromKeys = (dev: Device, accent: ThemeColor, chartColor: ThemeColor): DashWidget[] => {
    const keys = Object.keys(dev.data || {}).filter((k) => !/rssi/i.test(k));
    const w: DashWidget[] = [{ type: 'device_list', deviceSN: '*', field: '', title: th ? 'สถานะอุปกรณ์' : 'Device Status', size: 'w2', color: 'blue', x: 0, y: 0, w: 4, h: 8 }];
    keys.slice(0, 4).forEach((k, i) => {
      w.push({ type: 'single_card', deviceSN: dev.sn, field: k, title: `${dev.name} — ${k}`, size: 'w1', color: accent, x: (i % 2) * 4 + 4, y: Math.floor(i / 2) * 8, w: 4, h: 8 });
    });
    if (keys.length >= 2) {
      w.push({ type: 'line_chart', deviceSN: dev.sn, field: keys.slice(0, 2).join(','), title: th ? 'แนวโน้ม' : 'Trend', size: 'w2', color: chartColor, range: '1d', x: 0, y: 16, w: 12, h: 8 });
    }
    return w;
  };

  switch (solution) {
    case 'wwtp': {
      const dev = devs.find((d) => /mtech/i.test(d.name || '')) || pick(['water_level']);
      if (dev) return [{ type: 'wwtp_scada', deviceSN: dev.sn, field: '', title: th ? 'ระบบบำบัดน้ำเสีย (SCADA)' : 'Wastewater Treatment (SCADA)', size: 'w3', color: 'cyan', x: 0, y: 0, w: 12, h: 20 }];
      return null;
    }
    case 'solar': {
      const dev = pick(['inverter_solar']);
      if (dev) {
        const sn = dev.sn;
        return [
          { type: 'solar_flow', deviceSN: sn, field: '', title: th ? 'ผังพลังงานโซลาร์' : 'Solar Flow', size: 'w3', color: 'amber', x: 0, y: 0, w: 7, h: 14 },
          { type: 'solar_balance', deviceSN: sn, field: '', title: th ? 'สมดุลพลังงาน' : 'Energy Balance', size: 'w2', color: 'green', x: 7, y: 0, w: 5, h: 14 },
          { type: 'line_chart', deviceSN: sn, field: 'pv,to_grid', title: th ? 'การผลิต (kW)' : 'Production (kW)', size: 'w2', color: 'amber', range: '1d', x: 0, y: 14, w: 6, h: 8 },
          { type: 'gauge', deviceSN: sn, field: 'soc', title: th ? 'แบตเตอรี่ SoC' : 'Battery SoC', size: 'w1', color: 'green', gaugeMax: 100, x: 6, y: 14, w: 3, h: 8 },
          { type: 'single_card', deviceSN: sn, field: 'solar_today', title: th ? 'โซลาร์วันนี้' : 'Solar Today', size: 'w1', color: 'amber', x: 9, y: 14, w: 3, h: 8 },
        ];
      }
      return null;
    }
    case 'water': {
      const dev = pick(['water_level']);
      if (dev) {
        const sn = dev.sn;
        return [
          { type: 'scada', deviceSN: sn, field: '', title: th ? 'ผังระบบน้ำ (SCADA)' : 'Water System (SCADA)', size: 'w3', color: 'cyan', x: 0, y: 0, w: 12, h: 12 },
          { type: 'gauge', deviceSN: sn, field: 'level_ground', title: th ? 'ระดับถังพื้น' : 'Ground Tank', size: 'w1', color: 'blue', gaugeMax: 100, x: 0, y: 12, w: 3, h: 8 },
          { type: 'gauge', deviceSN: sn, field: 'level_champ', title: th ? 'ระดับถังแชมเปญ' : 'Elevated Tank', size: 'w1', color: 'cyan', gaugeMax: 100, x: 3, y: 12, w: 3, h: 8 },
          { type: 'line_chart', deviceSN: sn, field: 'level_ground,level_champ', title: th ? 'แนวโน้มระดับน้ำ' : 'Tank Level Trend', size: 'w2', color: 'cyan', range: '7d', x: 6, y: 12, w: 6, h: 8 },
        ];
      }
      return null;
    }
    case 'energy': {
      const dev = pick(['smart_meter', 'mdb_sensor', 'power_analyzer', 'energy_saver']);
      if (dev) return cardsFromKeys(dev, 'blue', 'blue');
      return null;
    }
    case 'environment':
    case 'coldchain': {
      const dev = pick(['temp_sensor', 'env_sensor', 'air_quality']);
      if (dev) return cardsFromKeys(dev, 'green', 'green');
      return null;
    }
    default:
      return null;
  }
}

interface AppState {
  // Auth
  currentUser: User | null;
  dbUser: DbUser | null;
  users: User[];
  plants: Record<string, Plant>;

  // Navigation
  currentPlant: string | null;
  page: PageId;
  lang: Lang;
  sidebarCollapsed: boolean;
  mobileNavOpen: boolean;

  // Dashboard
  dashConfigs: Record<string, DashWidget[]>;
  dashEditMode: boolean;

  // Device chart offcanvas
  selectedDevSN: string | null;
  // Fallback device object for the offcanvas when the selection isn't in
  // `plants`/devicePool (e.g. a row on the My Devices page). Set alongside
  // selectedDevSN by openDeviceChart's optional 2nd arg.
  chartDevice: Device | null;
  monitorDevSN: string | null;
  // Full device object to monitor when it isn't in the current plant's list
  // (e.g. opened from the cross-plant Device Overview) — lets Monitor run standalone.
  monitorDevice: Device | null;
  selectedField: string | null;
  chartRange: string;

  // Plant settings tab
  psTab: string;
  psProfileTab: string;

  // Toast
  toastMessage: string | null;
  toastKind: ToastKind;

  // Auth loading
  authLoading: boolean;

  // Actions
  login: (email: string, pass: string) => boolean;
  dbLogin: (dbUser: DbUser) => void;
  restoreSession: () => Promise<void>;
  loadPlants: () => Promise<void>;
  logout: () => void;
  setPage: (page: PageId) => void;
  setCurrentPlant: (pid: string | null) => void;
  switchPlant: (pid: string | null) => void;
  setMobileNav: (open: boolean) => void;
  toggleMobileNav: () => void;
  toggleLang: () => void;
  toggleSidebar: () => void;
  toggleTheme: () => void;
  toast: (msg: string, kind?: ToastKind) => void;

  // Plant actions
  createPlant: (plant: Plant) => void;
  createPlantDb: (input: { name: string; plant_type: string; address: string; lat: number; lng: number; solution?: string }) => Promise<{ ok: boolean; error?: string }>;
  updatePlant: (pid: string, updates: Partial<Plant>) => void;
  updatePlantAccess: (pid: string, access: { viewer?: string; admin?: string; owner?: string }) => Promise<{ ok: boolean; error?: string }>;
  deletePlant: (pid: string) => Promise<{ ok: boolean; error?: string }>;

  // Device actions
  registerDevice: (device: Device) => void;
  addDeviceToPlant: (pid: string, poolIndices: number[]) => void;
  linkDeviceByMacId: (pid: string, macId: string) => Promise<{ ok: boolean; error?: string }>;
  saveDormConfig: (pid: string, dorm: import('@/lib/types').DormConfig) => Promise<{ ok: boolean; error?: string }>;
  // Broiler farm: start/close a flock or save alert settings (see /api/projects/[id]/poultry).
  savePoultry: (pid: string, body: Record<string, unknown>) => Promise<{ ok: boolean; error?: string }>;
  unlinkDeviceFromPlant: (pid: string, macId: string) => Promise<{ ok: boolean; error?: string }>;
  removeDeviceFromPlant: (pid: string, devIdx: number) => void;
  toggleDeviceStatus: (pid: string, devIdx: number) => void;
  toggleDeviceAI: (pid: string, devIdx: number) => void;
  updateDeviceThresholds: (pid: string, devIdx: number, thresholds: Record<string, { op: string; val: number }>) => void;
  renameDevice: (pid: string, devIdx: number, newName: string) => void;

  // Dashboard actions
  setDashEditMode: (mode: boolean) => void;
  loadWidgets: (pid: string) => Promise<void>;
  saveWidgets: (pid: string) => void;
  addWidget: (pid: string, widget: DashWidget) => void;
  updateWidget: (pid: string, idx: number, widget: DashWidget) => void;
  removeWidget: (pid: string, idx: number) => void;
  reorderWidgets: (pid: string, fromIdx: number, toIdx: number) => void;
  updateWidgetLayout: (pid: string, idx: number, layout: { x: number; y: number; w: number; h: number }) => void;
  applyWidgetLayouts: (pid: string, layouts: Array<{ idx: number; x: number; y: number; w: number; h: number }>) => void;
  resetDashboard: (pid: string) => void;
  getDefaultWidgets: (pid: string) => DashWidget[];

  // Offcanvas
  openDeviceChart: (sn: string, device?: Device) => void;
  // `target` picks which Monitor entry lights up in the sidebar: 'monitor2' is
  // the global Device Monitor (cross-plant pages), 'monitor' is the one nested
  // under the plant. Both render the same page, so only the nav highlight and
  // the user's sense of place differ. Defaults to the global one.
  openInMonitor: (sn: string, plantId?: string, device?: Device, target?: 'monitor' | 'monitor2') => void;
  closeOffcanvas: () => void;
  setSelectedField: (field: string) => void;
  setChartRange: (range: string) => void;

  // Tabs
  setPsTab: (tab: string) => void;
  setPsProfileTab: (tab: string) => void;

  // User management
  addUser: (user: User) => void;
  updateUser: (idx: number, updates: Partial<User>) => void;
  deleteUser: (idx: number) => void;

  // Profile
  updateProfile: (updates: Partial<User>) => void;
  changePassword: (oldPass: string, newPass: string) => boolean;

  // Plant AI/Notification config
  updatePlantAIConfig: (pid: string, config: Partial<Plant['aiConfig']>) => void;
  savePlantAIConfig: (pid: string) => Promise<{ ok: boolean; error?: string }>;
  updatePlantNotifications: (pid: string, notifications: Plant['notifications']) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  currentUser: null,
  dbUser: null,
  users: [...INITIAL_USERS],
  plants: {},
  currentPlant: null,
  page: 'overview',
  lang: 'en',
  sidebarCollapsed: false,
  mobileNavOpen: false,
  dashConfigs: {},
  dashEditMode: false,
  selectedDevSN: null,
  chartDevice: null,
  monitorDevSN: null,
  monitorDevice: null,
  selectedField: null,
  chartRange: '14d',
  psTab: 'general',
  psProfileTab: 'info',
  toastMessage: null,
  toastKind: 'info',
  authLoading: true,

  // Auth
  login: (email, pass) => {
    const user = get().users.find((u) => u.email === email && u.pass === pass);
    if (!user) return false;
    // Demo mode has no API — seed the store with the demo plants this user can
    // access so the dashboard/AI/overview have data to render.
    const plants: Record<string, Plant> = {};
    for (const pid of user.plants) {
      if (INITIAL_PLANTS[pid]) plants[pid] = INITIAL_PLANTS[pid];
    }
    // Viewers have no plant picker in the sidebar, so auto-select their first
    // plant and land on its dashboard; otherwise they'd be stuck on the
    // "no site assigned" screen. Other roles start on the all-plants overview.
    const isViewer = user.role === 'viewer';
    const firstPlant = user.plants.find((pid) => plants[pid]) || null;
    set({
      currentUser: user,
      plants,
      page: isViewer && firstPlant ? 'dashboard' : 'overview',
      currentPlant: isViewer ? firstPlant : null,
    });
    return true;
  },
  dbLogin: (dbUser) => {
    const fakeUser: User = {
      email: dbUser.email,
      pass: '',
      name: dbUser.name,
      role: userlevelToRole(dbUser.userlevel),
      plants: [],
      devicePool: [],
    };
    // Viewers land directly inside their own plant (they have no all-plants
    // overview). Keep the loading screen up until loadPlants() has selected
    // it, so they never see an empty sidebar while /api/projects is loading.
    const isViewer = fakeUser.role === 'viewer';
    set({ currentUser: fakeUser, dbUser, page: 'overview', currentPlant: null, authLoading: isViewer });
    track('login');
    const plantsReady = get().loadPlants();
    if (isViewer) plantsReady.then(() => set({ authLoading: false }));
  },
  restoreSession: async () => {
    try {
      const res = await fetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        const fakeUser: User = {
          email: data.email,
          pass: '',
          name: data.name,
          role: userlevelToRole(data.userlevel),
          plants: [],
          devicePool: [],
        };
        // Viewers land directly inside their own plant (no all-plants overview),
        // so keep the loading screen up until loadPlants() has selected it —
        // otherwise they briefly see an empty sidebar while /api/projects loads.
        const isViewer = fakeUser.role === 'viewer';
        set({ currentUser: fakeUser, dbUser: data, page: 'overview', authLoading: isViewer });
        // Load plants and preferences from database after session restore
        const plantsReady = get().loadPlants();
        // Load user preferences (theme, sidebar)
        fetch('/api/preferences').then(r => r.json()).then(prefs => {
          if (prefs.sidebar_collapsed !== undefined) set({ sidebarCollapsed: prefs.sidebar_collapsed });
          if (prefs.theme && typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', prefs.theme);
          }
        }).catch(() => {});
        if (isViewer) {
          await plantsReady;
          set({ authLoading: false });
        }
      } else {
        set({ authLoading: false });
      }
    } catch {
      set({ authLoading: false });
    }
  },
  loadPlants: async () => {
    try {
      const res = await fetch('/api/projects');
      if (res.ok) {
        const dbPlants = await res.json() as Plant[];
        if (dbPlants.length > 0) {
          const plantsMap: Record<string, Plant> = {};
          const plantIds: string[] = [];
          for (const p of dbPlants) {
            // Re-attach locally-persisted notifications (API doesn't return them).
            const savedNotif = loadNotifications(p.id);
            plantsMap[p.id] = savedNotif ? { ...p, notifications: savedNotif } : p;
            plantIds.push(p.id);
          }
          set((state) => ({
            plants: { ...state.plants, ...plantsMap },
            currentUser: state.currentUser ? { ...state.currentUser, plants: [...new Set([...state.currentUser.plants, ...plantIds])] } : state.currentUser,
          }));
          // Viewers have no all-plants overview: land them straight in their
          // own plant. With a single plant there is nothing to choose.
          const st = get();
          if (st.currentUser?.role === 'viewer' && !st.currentPlant && plantIds.length > 0) {
            st.switchPlant(plantIds[0]);
          }
        }
      }
    } catch {
      // Silently fall back to demo data
    }
  },
  logout: () => {
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    set({ currentUser: null, dbUser: null, currentPlant: null, page: 'overview' });
  },

  // Navigation — any page/plant change also closes the mobile nav drawer.
  setPage: (page) => set({ page, mobileNavOpen: false }),
  setCurrentPlant: (pid) => set({ currentPlant: pid }),
  setMobileNav: (open) => set({ mobileNavOpen: open }),
  toggleMobileNav: () => set((s) => ({ mobileNavOpen: !s.mobileNavOpen })),
  switchPlant: (pid) => {
    if (pid) {
      // Dorm-mode plants land on the dorm overview instead of the widget dashboard.
      const isDorm = !!get().plants[pid]?.dorm?.enabled;
      set({ currentPlant: pid, page: isDorm ? 'dormoverview' : 'dashboard', mobileNavOpen: false });
      // Load widgets from database for this plant
      get().loadWidgets(pid);
    } else {
      set({ currentPlant: null, page: 'overview', mobileNavOpen: false });
    }
  },
  toggleLang: () => set((s) => ({ lang: s.lang === 'th' ? 'en' : 'th' })),
  toggleSidebar: () => {
    // Compute the new value once and persist THAT. Reading the store back after
    // set() and negating it saved the state the user had just left — zustand's
    // set is synchronous, so get() already returned the new value — which made
    // every refresh undo the last toggle.
    const collapsed = !get().sidebarCollapsed;
    set({ sidebarCollapsed: collapsed });
    // Persist to DB
    if (get().dbUser) {
      fetch('/api/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sidebar_collapsed: collapsed }),
      }).catch(() => {});
    }
  },
  toggleTheme: () => {
    if (typeof document !== 'undefined') {
      const current = document.documentElement.getAttribute('data-theme');
      const newTheme = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', newTheme);
      // Persist to DB
      if (get().dbUser) {
        fetch('/api/preferences', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme: newTheme }),
        }).catch(() => {});
      }
    }
  },
  /*
   * kind ตัดสินว่า popup จะขึ้นเขียว (สำเร็จ) แดง (ล้มเหลว) หรือทอง (แจ้งให้ทราบ)
   *
   * default เป็น 'info' ไม่ใช่ 'success' โดยเจตนา — จุดที่เรียก toast() มีอยู่
   * กว่า 60 แห่งและยังไม่ได้ระบุ kind ทุกแห่ง ถ้า default เป็นสำเร็จ ข้อความ
   * ที่แจ้งว่าล้มเหลวจะขึ้นติ๊กถูกสีเขียวซึ่งเป็นการโกหกผู้ใช้
   */
  toast: (msg, kind = 'info') => {
    set({ toastMessage: msg, toastKind: kind });
    setTimeout(() => set({ toastMessage: null }), kind === 'error' ? 5000 : 3000);
  },

  // Plant actions
  createPlant: (plant) => set((s) => {
    const newPlants = { ...s.plants, [plant.id]: plant };
    const newUser = s.currentUser ? { ...s.currentUser, plants: [...s.currentUser.plants, plant.id] } : s.currentUser;
    return { plants: newPlants, currentUser: newUser };
  }),
  createPlantDb: async (input) => {
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: input.name,
          plant_type: input.plant_type,
          address: input.address,
          lat: input.lat,
          lng: input.lng,
          config_data: { meters: [], solution: input.solution || null },
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      await get().loadPlants();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  },
  updatePlant: (pid, updates) => set((s) => ({
    plants: { ...s.plants, [pid]: { ...s.plants[pid], ...updates } },
  })),
  updatePlantAccess: async (pid, access) => {
    try {
      const res = await fetch(`/api/projects/${pid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(access),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      await get().loadPlants();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  },
  /**
   * Delete a plant for good.
   *
   * This used to be a bare `set()`, so the row survived and the plant came
   * back on the next load — the button looked like it worked. In DB mode the
   * DELETE has to land before the local state drops it, or a failure (403 for
   * anyone but the owner) leaves the UI claiming a deletion the server refused.
   *
   * Demo mode has no server, so it clears local state as before.
   *
   * Note: /api/projects/[id] removes the tbl_cfg_projects_v4 row only. Baseline
   * data tables and their tbl_project_datatable_schema rows are keyed by a
   * project token rather than the id and are left behind.
   */
  deletePlant: async (pid) => {
    const drop = () => set((s) => {
      const newPlants = { ...s.plants };
      delete newPlants[pid];
      const newUser = s.currentUser ? { ...s.currentUser, plants: s.currentUser.plants.filter((p) => p !== pid) } : s.currentUser;
      return { plants: newPlants, currentUser: newUser, currentPlant: null, page: 'plants' };
    });

    if (!get().dbUser) {
      drop();
      return { ok: true };
    }

    try {
      const res = await fetch(`/api/projects/${pid}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      drop();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  },

  // Device actions
  registerDevice: (device) => set((s) => {
    if (!s.currentUser) return s;
    const newPool = [...(s.currentUser.devicePool || []), device];
    return { currentUser: { ...s.currentUser, devicePool: newPool } };
  }),
  addDeviceToPlant: (pid, poolIndices) => set((s) => {
    if (!s.currentUser) return s;
    const pool = [...(s.currentUser.devicePool || [])];
    const plant = { ...s.plants[pid], devices: [...s.plants[pid].devices] };
    const sorted = [...poolIndices].sort((a, b) => b - a);
    sorted.forEach((idx) => {
      plant.devices.push(pool[idx]);
      pool.splice(idx, 1);
    });
    return {
      plants: { ...s.plants, [pid]: plant },
      currentUser: { ...s.currentUser, devicePool: pool },
    };
  }),
  linkDeviceByMacId: async (pid, macId) => {
    const plant = get().plants[pid];
    if (!plant) return { ok: false, error: 'Plant not found' };
    const currentMacs = plant.devices.map((d) => d.sn);
    if (currentMacs.includes(macId)) return { ok: false, error: 'Already linked' };
    const newMeters = [...currentMacs, macId].map((m) => ({ mac_id: m }));
    try {
      const res = await fetch(`/api/projects/${pid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_data: { meters: newMeters } }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      await get().loadPlants();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  },
  // Persist the full dorm config (rooms/tenants/rates). config_data PUT shallow-merges
  // top-level keys, so always send the complete dorm object.
  savePoultry: async (pid, body) => {
    try {
      const res = await fetch(`/api/projects/${pid}/poultry`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: data.error || `HTTP ${res.status}` };
      const plant = get().plants[pid];
      if (plant) set((s) => ({ plants: { ...s.plants, [pid]: { ...plant, poultry: data.poultry } } }));
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  },
  saveDormConfig: async (pid, dorm) => {
    try {
      const res = await fetch(`/api/projects/${pid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_data: { dorm } }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      await get().loadPlants();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  },
  unlinkDeviceFromPlant: async (pid, macId) => {
    const plant = get().plants[pid];
    if (!plant) return { ok: false, error: 'Plant not found' };
    const newMeters = plant.devices
      .filter((d) => d.sn !== macId)
      .map((d) => ({ mac_id: d.sn }));
    try {
      const res = await fetch(`/api/projects/${pid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_data: { meters: newMeters } }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      await get().loadPlants();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  },
  removeDeviceFromPlant: (pid, devIdx) => set((s) => {
    if (!s.currentUser) return s;
    const plant = { ...s.plants[pid], devices: [...s.plants[pid].devices] };
    const dev = plant.devices[devIdx];
    plant.devices.splice(devIdx, 1);
    const newPool = [...(s.currentUser.devicePool || []), dev];
    return {
      plants: { ...s.plants, [pid]: plant },
      currentUser: { ...s.currentUser, devicePool: newPool },
    };
  }),
  toggleDeviceStatus: (pid, devIdx) => set((s) => {
    const plant = { ...s.plants[pid], devices: [...s.plants[pid].devices] };
    const dev = { ...plant.devices[devIdx] };
    dev.st = dev.st === 'online' ? 'offline' : 'online';
    plant.devices[devIdx] = dev;
    return { plants: { ...s.plants, [pid]: plant } };
  }),
  toggleDeviceAI: (pid, devIdx) => set((s) => {
    const plant = { ...s.plants[pid], devices: [...s.plants[pid].devices] };
    const dev = { ...plant.devices[devIdx] };
    dev.aiEnabled = dev.aiEnabled === false ? true : false;
    plant.devices[devIdx] = dev;
    return { plants: { ...s.plants, [pid]: plant } };
  }),
  updateDeviceThresholds: (pid, devIdx, thresholds) => set((s) => {
    const plant = { ...s.plants[pid], devices: [...s.plants[pid].devices] };
    const dev = { ...plant.devices[devIdx] };
    dev.thresholds = thresholds as Record<string, { op: '>' | '<' | '='; val: number }>;
    plant.devices[devIdx] = dev;
    return { plants: { ...s.plants, [pid]: plant } };
  }),
  renameDevice: (pid, devIdx, newName) => set((s) => {
    const plant = { ...s.plants[pid], devices: [...s.plants[pid].devices] };
    plant.devices[devIdx] = { ...plant.devices[devIdx], name: newName };
    return { plants: { ...s.plants, [pid]: plant } };
  }),

  // Dashboard
  setDashEditMode: (mode) => set({ dashEditMode: mode }),
  getDefaultWidgets: (pid) => {
    const plants = get().plants;
    const devs = plants[pid]?.devices || [];
    const th = get().lang === 'th';

    // Solution-first (pillar ③ · slice 2): if the plant declares a solution,
    // provision a preset shaped by it. Returns null to fall through to the
    // legacy device heuristics below (non-breaking for solution-less plants).
    const solution = plants[pid]?.solution;
    if (solution) {
      const preset = solutionPreset(solution, devs, th);
      if (preset) return preset;
    }

    // M-TECH wastewater treatment plant (เทศบาลนครแหลมฉบัง): a single full-width
    // WWTP SCADA schematic (pump station + water quality + energy). Its devices
    // are named Mtech* and are NOT the level_ground/champ water_level type the
    // generic `scada` branch below detects, so match this plant explicitly and
    // return just the wwtp_scada widget — otherwise it falls through to the
    // generic device-card fallback (the ~19-card layout).
    const wwtpDev = devs.find((d) => /mtech/i.test(d.name || ''));
    if (wwtpDev) {
      return [
        { type: 'wwtp_scada', deviceSN: wwtpDev.sn, field: '', title: th ? 'ระบบบำบัดน้ำเสีย (SCADA)' : 'Wastewater Treatment (SCADA)', size: 'w3', color: 'cyan', x: 0, y: 0, w: 12, h: 20 },
      ];
    }

    // SCADA water-system plants (Mspace V6): a tailored schematic-first
    // dashboard instead of the generic single-card layout.
    const scadaDev = devs.find(
      (d) => d.type === 'water_level' && (d.data?.level_ground !== undefined || d.data?.level_champ !== undefined),
    );
    if (scadaDev) {
      const sn = scadaDev.sn;
      return [
        { type: 'scada', deviceSN: sn, field: '', title: th ? 'ผังระบบน้ำ (SCADA)' : 'Water System (SCADA)', size: 'w3', color: 'cyan', x: 0, y: 0, w: 12, h: 12 },
        { type: 'gauge', deviceSN: sn, field: 'level_ground', title: th ? 'ระดับถังพื้น' : 'Ground Tank', size: 'w1', color: 'blue', gaugeMax: 100, x: 0, y: 12, w: 3, h: 8 },
        { type: 'gauge', deviceSN: sn, field: 'level_champ', title: th ? 'ระดับถังแชมเปญ' : 'Elevated Tank', size: 'w1', color: 'cyan', gaugeMax: 100, x: 3, y: 12, w: 3, h: 8 },
        { type: 'line_chart', deviceSN: sn, field: 'level_ground,level_champ', title: th ? 'แนวโน้มระดับน้ำ' : 'Tank Level Trend', size: 'w2', color: 'cyan', range: '7d', x: 6, y: 12, w: 6, h: 8 },
        { type: 'status', deviceSN: sn, field: 'pump1', title: th ? 'สถานะปั๊ม 1' : 'Pump 1', size: 'w1', color: 'green', x: 0, y: 20, w: 3, h: 6, statusThresholds: { lowAt: 1, lowColor: 'blue', lowLabel: th ? 'หยุด' : 'STOP', highAt: 0, highColor: 'green', highLabel: th ? 'ทำงาน' : 'RUN' } },
        { type: 'status', deviceSN: sn, field: 'pump2', title: th ? 'สถานะปั๊ม 2' : 'Pump 2', size: 'w1', color: 'green', x: 3, y: 20, w: 3, h: 6, statusThresholds: { lowAt: 1, lowColor: 'blue', lowLabel: th ? 'หยุด' : 'STOP', highAt: 0, highColor: 'green', highLabel: th ? 'ทำงาน' : 'RUN' } },
        { type: 'status', deviceSN: sn, field: 'ovl1', title: th ? 'โอเวอร์โหลด ปั๊ม 1' : 'Overload P1', size: 'w1', color: 'red', x: 6, y: 20, w: 3, h: 6, statusThresholds: { highAt: 0, highColor: 'red', highLabel: 'TRIP', midColor: 'green', midLabel: 'OK' } },
        { type: 'status', deviceSN: sn, field: 'ovl2', title: th ? 'โอเวอร์โหลด ปั๊ม 2' : 'Overload P2', size: 'w1', color: 'red', x: 9, y: 20, w: 3, h: 6, statusThresholds: { highAt: 0, highColor: 'red', highLabel: 'TRIP', midColor: 'green', midLabel: 'OK' } },
      ];
    }

    // K-saver (energy_saver) sites: a savings-comparison-first dashboard built
    // around the K-saver widget instead of the generic single-card layout.
    const ksaverDev = devs.find((d) => d.type === 'energy_saver');
    if (ksaverDev) {
      const sn = ksaverDev.sn;
      return [
        { type: 'ksaver', deviceSN: sn, field: '', title: th ? 'ผังการประหยัดพลังงาน K-saver' : 'K-saver Energy-Saving Schematic', size: 'w3', color: 'green', x: 0, y: 0, w: 12, h: 18 },
        { type: 'gauge', deviceSN: sn, field: 'save_pct', title: th ? '% ที่ประหยัดได้' : 'Energy Saved', size: 'w1', color: 'green', gaugeMax: 20, x: 0, y: 18, w: 3, h: 7 },
        { type: 'single_card', deviceSN: sn, field: 'save_kw', title: th ? 'กำลังไฟที่ลดได้' : 'Power Saved', size: 'w1', color: 'green', x: 3, y: 18, w: 3, h: 7 },
        { type: 'single_card', deviceSN: sn, field: 'p_bypass', title: th ? 'กำลังไฟ (Bypass)' : 'Power (By-pass)', size: 'w1', color: 'red', x: 6, y: 18, w: 3, h: 7 },
        { type: 'single_card', deviceSN: sn, field: 'p_save', title: th ? 'กำลังไฟ (K-saver)' : 'Power (K-saver)', size: 'w1', color: 'green', x: 9, y: 18, w: 3, h: 7 },
      ];
    }

    // Solar + battery (inverter_solar): Sigen-style flow + balance dashboard.
    const solarDev = devs.find((d) => d.type === 'inverter_solar');
    if (solarDev) {
      const sn = solarDev.sn;
      return [
        { type: 'solar_flow', deviceSN: sn, field: '', title: th ? 'ผังพลังงานโซลาร์' : 'Solar Flow', size: 'w3', color: 'amber', x: 0, y: 0, w: 7, h: 14 },
        { type: 'solar_balance', deviceSN: sn, field: '', title: th ? 'สมดุลพลังงาน' : 'Energy Balance', size: 'w2', color: 'green', x: 7, y: 0, w: 5, h: 14 },
        { type: 'line_chart', deviceSN: sn, field: 'pv,to_grid', title: th ? 'การผลิต (kW)' : 'Production (kW)', size: 'w2', color: 'amber', range: '1d', x: 0, y: 14, w: 6, h: 8 },
        { type: 'line_chart', deviceSN: sn, field: 'home,from_grid', title: th ? 'การใช้ไฟ (kW)' : 'Consumption (kW)', size: 'w2', color: 'blue', range: '1d', x: 6, y: 14, w: 6, h: 8 },
        { type: 'single_card', deviceSN: sn, field: 'solar_today', title: th ? 'โซลาร์วันนี้' : 'Solar Generated', size: 'w1', color: 'amber', x: 0, y: 22, w: 3, h: 6 },
        { type: 'gauge', deviceSN: sn, field: 'soc', title: th ? 'แบตเตอรี่ SoC' : 'Battery SoC', size: 'w1', color: 'green', gaugeMax: 100, x: 3, y: 22, w: 3, h: 6 },
        { type: 'single_card', deviceSN: sn, field: 'self_powered', title: th ? 'พึ่งพาตัวเอง' : 'Self-Powered', size: 'w1', color: 'green', x: 6, y: 22, w: 3, h: 6 },
        { type: 'single_card', deviceSN: sn, field: 'home_today', title: th ? 'บ้านใช้วันนี้' : 'Home Usage', size: 'w1', color: 'blue', x: 9, y: 22, w: 3, h: 6 },
      ];
    }

    const w: DashWidget[] = [];
    w.push({ type: 'device_list', deviceSN: '*', field: '', title: 'Device Status', size: 'w2', color: 'blue' });
    devs.forEach((d) => {
      const dt = DEV_TYPES[d.type];
      if (!dt) return;
      const keys = Object.keys(d.data || {});
      if (keys.length > 0) {
        const k = keys[0];
        const fm = { label: k, unit: '', color: dt.color };
        w.push({ type: 'single_card', deviceSN: d.sn, field: k, title: d.name + ' — ' + fm.label, size: 'w1', color: fm.color });
      }
      if (keys.length >= 3) {
        w.push({ type: 'card', deviceSN: d.sn, field: keys.slice(0, 3).join(','), title: d.name, size: 'w2', color: dt.color });
      }
    });
    return w;
  },
  loadWidgets: async (pid) => {
    if (!get().dbUser) return;
    try {
      const res = await fetch(`/api/projects/${pid}/widgets`);
      if (res.ok) {
        const widgets = await res.json();
        if (Array.isArray(widgets) && widgets.length > 0) {
          set((s) => ({ dashConfigs: { ...s.dashConfigs, [pid]: widgets } }));
        }
      }
    } catch {
      // Silently fall back
    }
  },
  saveWidgets: (pid) => {
    // usage analytics: one 'widget_save' per minute at most (drag/resize saves often)
    if (Date.now() - lastWidgetTrack > 60_000) { lastWidgetTrack = Date.now(); track('action', { action: 'widget_save', plant: /^\d+$/.test(pid) ? Number(pid) : undefined }); }
    if (!get().dbUser) return;
    const widgets = get().dashConfigs[pid];
    if (!widgets) return;
    fetch(`/api/projects/${pid}/widgets`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(widgets),
    }).catch(() => {});
  },
  addWidget: (pid, widget) => {
    set((s) => {
      const configs = { ...s.dashConfigs };
      if (!configs[pid]) configs[pid] = get().getDefaultWidgets(pid);
      configs[pid] = [...configs[pid], widget];
      return { dashConfigs: configs };
    });
    get().saveWidgets(pid);
  },
  updateWidget: (pid, idx, widget) => {
    set((s) => {
      const configs = { ...s.dashConfigs };
      if (!configs[pid]) return s;
      configs[pid] = [...configs[pid]];
      configs[pid][idx] = widget;
      return { dashConfigs: configs };
    });
    get().saveWidgets(pid);
  },
  removeWidget: (pid, idx) => {
    // Clean up the external image store for image widgets so the picture isn't
    // orphaned on the backend after the widget is gone.
    const removed = get().dashConfigs[pid]?.[idx];
    if (removed?.type === 'image' && removed.id && get().dbUser) {
      deleteWidgetImage(pid, removed.id);
    }
    set((s) => {
      const configs = { ...s.dashConfigs };
      if (!configs[pid]) return s;
      configs[pid] = configs[pid].filter((_, i) => i !== idx);
      return { dashConfigs: configs };
    });
    get().saveWidgets(pid);
  },
  updateWidgetLayout: (pid, idx, layout) => {
    set((s) => {
      const configs = { ...s.dashConfigs };
      if (!configs[pid]) return s;
      configs[pid] = [...configs[pid]];
      const w = { ...configs[pid][idx], ...layout };
      w.size = layout.w <= 4 ? 'w1' : layout.w <= 8 ? 'w2' : 'w3';
      configs[pid][idx] = w;
      return { dashConfigs: configs };
    });
    get().saveWidgets(pid);
  },
  applyWidgetLayouts: (pid, layouts) => {
    let changed = false;
    set((s) => {
      const arr = s.dashConfigs[pid];
      if (!arr) return s;
      const next = [...arr];
      for (const l of layouts) {
        const cur = next[l.idx];
        if (!cur) continue;
        if (cur.x === l.x && cur.y === l.y && cur.w === l.w && cur.h === l.h) continue;
        changed = true;
        const size: 'w1' | 'w2' | 'w3' = l.w <= 4 ? 'w1' : l.w <= 8 ? 'w2' : 'w3';
        next[l.idx] = { ...cur, x: l.x, y: l.y, w: l.w, h: l.h, size };
      }
      if (!changed) return s;
      return { dashConfigs: { ...s.dashConfigs, [pid]: next } };
    });
    if (changed) get().saveWidgets(pid);
  },
  reorderWidgets: (pid, fromIdx, toIdx) => {
    set((s) => {
      const configs = { ...s.dashConfigs };
      if (!configs[pid]) return s;
      const arr = [...configs[pid]];
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      configs[pid] = arr;
      return { dashConfigs: configs };
    });
    get().saveWidgets(pid);
  },
  resetDashboard: (pid) => {
    set((s) => {
      const configs = { ...s.dashConfigs };
      configs[pid] = get().getDefaultWidgets(pid);
      return { dashConfigs: configs, dashEditMode: false };
    });
    get().saveWidgets(pid);
  },

  // Offcanvas
  openDeviceChart: (sn, device) => set({ selectedDevSN: sn, chartDevice: device ?? null }),
  openInMonitor: (sn, plantId, device, target) => set(
    plantId
      ? { monitorDevSN: sn, monitorDevice: device ?? null, currentPlant: plantId, page: target ?? 'monitor2' }
      : { monitorDevSN: sn, monitorDevice: device ?? null, page: target ?? 'monitor2' }
  ),
  closeOffcanvas: () => set({ selectedDevSN: null, chartDevice: null }),
  setSelectedField: (field) => set({ selectedField: field }),
  setChartRange: (range) => set({ chartRange: range }),

  // Tabs
  setPsTab: (tab) => set({ psTab: tab }),
  setPsProfileTab: (tab) => set({ psProfileTab: tab }),

  // User management
  addUser: (user) => set((s) => ({ users: [...s.users, user] })),
  updateUser: (idx, updates) => set((s) => {
    const users = [...s.users];
    users[idx] = { ...users[idx], ...updates };
    return { users };
  }),
  deleteUser: (idx) => set((s) => ({
    users: s.users.filter((_, i) => i !== idx),
  })),

  // Profile
  updateProfile: (updates) => set((s) => {
    if (!s.currentUser) return s;
    return { currentUser: { ...s.currentUser, ...updates } };
  }),
  changePassword: (oldPass, newPass) => {
    const user = get().currentUser;
    if (!user || user.pass !== oldPass) return false;
    set({ currentUser: { ...user, pass: newPass } });
    return true;
  },

  // Plant config
  updatePlantAIConfig: (pid, config) => set((s) => {
    const plant = s.plants[pid];
    if (!plant) return s;
    const aiConfig = { ...(plant.aiConfig || { enabled: true, notify: { app: true, telegram: true }, sensitivity: 'medium' as const, schedule: 'daily' as const }), ...config };
    return { plants: { ...s.plants, [pid]: { ...plant, aiConfig } } };
  }),
  /*
   * เขียนค่า AI Config ที่อยู่ใน store ลง DB — เรียกจากปุ่ม Save
   *
   * แยกจาก updatePlantAIConfig เพราะการสลับสวิตช์แต่ละครั้งไม่ควรยิง DB
   * (คนกดสลับดูหลายรอบก่อนตัดสินใจ) และตัวเรียกต้องรู้ว่าสำเร็จจริงหรือไม่
   * จึงคืนผลลัพธ์ออกไป ไม่ใช่ fetch แบบทิ้งผล — ปุ่มที่บอก "บันทึกแล้ว"
   * ทุกครั้งโดยไม่ดู response แย่กว่าไม่มีปุ่มเลย
   */
  savePlantAIConfig: async (pid) => {
    const s = get();
    const plant = s.plants[pid];
    if (!plant) return { ok: false, error: 'plant not found' };
    // โหมดเดโม (ไม่ได้ล็อกอินเข้า DB) เก็บได้แค่ใน memory — ไม่มีที่ให้เขียน
    if (!s.dbUser) return { ok: true };
    const aiConfig = plant.aiConfig || { enabled: true, notify: { app: true, telegram: true }, sensitivity: 'medium' as const, schedule: 'daily' as const };
    try {
      const res = await fetch(`/api/projects/${pid}/aiconfig`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aiConfig }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        return { ok: false, error: d.error || `HTTP ${res.status}` };
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'network' };
    }
  },
  updatePlantNotifications: (pid, notifications) => set((s) => {
    const plant = s.plants[pid];
    if (!plant) return s;
    // Fast client cache (survives refresh / HMR)…
    if (notifications) saveNotifications(pid, notifications);
    if (notifications && s.dbUser) track('action', { action: 'notification_save', plant: /^\d+$/.test(pid) ? Number(pid) : undefined });
    // …and persist to the DB for logged-in users so the 24/7 cron engine sees it.
    if (notifications && s.dbUser) {
      fetch(`/api/projects/${pid}/notifications`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notifications }),
      }).catch(() => {});
    }
    return { plants: { ...s.plants, [pid]: { ...plant, notifications } } };
  }),
}));
